// src/components/BotManagersPanel.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import apiFetch from '../utils/api';

const ROLE_COLORS = {
  viewer: 'default',
  operator: 'info',
  manager: 'warning',
};

function BotManagersPanel({ namespace, botName, isOwner }) {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDiscordId, setAddDiscordId] = useState('');
  const [addUsername, setAddUsername] = useState('');
  const [addRole, setAddRole] = useState('viewer');
  const [adding, setAdding] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editManager, setEditManager] = useState(null);
  const [editRole, setEditRole] = useState('viewer');
  const [editing, setEditing] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteManager, setDeleteManager] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchManagers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/bots/${namespace}/${botName}/managers`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setManagers(data);
      } else {
        throw new Error('Failed to fetch managers');
      }
    } catch (err) {
      console.error('Error fetching managers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [namespace, botName]);

  useEffect(() => {
    if (isOwner) {
      fetchManagers();
    }
  }, [isOwner, fetchManagers]);

  const handleAdd = async () => {
    if (!addDiscordId.trim()) {
      setError('Discord ID is required');
      return;
    }

    setAdding(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/bots/${namespace}/${botName}/managers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          discordId: addDiscordId.trim(),
          username: addUsername.trim(),
          role: addRole,
        }),
      });

      if (response.ok) {
        const newManager = await response.json();
        setManagers([...managers, newManager]);
        setAddDialogOpen(false);
        setAddDiscordId('');
        setAddUsername('');
        setAddRole('viewer');
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add manager');
      }
    } catch (err) {
      console.error('Error adding manager:', err);
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleEditOpen = (manager) => {
    setEditManager(manager);
    setEditRole(manager.role);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editManager) return;

    setEditing(true);
    setError(null);

    try {
      const response = await apiFetch(
        `/api/bots/${namespace}/${botName}/managers/${editManager._id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ role: editRole }),
        }
      );

      if (response.ok) {
        const updated = await response.json();
        setManagers(managers.map((m) => (m._id === editManager._id ? updated : m)));
        setEditDialogOpen(false);
        setEditManager(null);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update manager role');
      }
    } catch (err) {
      console.error('Error updating manager:', err);
      setError(err.message);
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteOpen = (manager) => {
    setDeleteManager(manager);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteManager) return;

    setDeleting(true);
    setError(null);

    try {
      const response = await apiFetch(
        `/api/bots/${namespace}/${botName}/managers/${deleteManager._id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (response.ok) {
        setManagers(managers.filter((m) => m._id !== deleteManager._id));
        setDeleteDialogOpen(false);
        setDeleteManager(null);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove manager');
      }
    } catch (err) {
      console.error('Error removing manager:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (!isOwner) {
    return null;
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Bot Managers</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Manager
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : managers.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
            No managers added yet. Add managers to allow others to access this bot.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Discord User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Granted By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {managers.map((manager) => (
                  <TableRow key={manager._id}>
                    <TableCell>
                      <Typography variant="body2">
                        {manager.username || manager.discordId}
                      </Typography>
                      {manager.username && (
                        <Typography variant="caption" color="textSecondary" fontFamily="monospace">
                          {manager.discordId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={manager.role}
                        color={ROLE_COLORS[manager.role] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {manager.grantedBy || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditOpen(manager)}
                        title="Edit role"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteOpen(manager)}
                        title="Remove manager"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add Manager Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={() => !adding && setAddDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Manager</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Discord ID"
              type="text"
              fullWidth
              variant="outlined"
              value={addDiscordId}
              onChange={(e) => setAddDiscordId(e.target.value)}
              disabled={adding}
              sx={{ mb: 2 }}
              helperText="The Discord user ID of the manager"
            />
            <TextField
              margin="dense"
              label="Username (optional)"
              type="text"
              fullWidth
              variant="outlined"
              value={addUsername}
              onChange={(e) => setAddUsername(e.target.value)}
              disabled={adding}
              sx={{ mb: 2 }}
              helperText="Display name for reference"
            />
            <FormControl fullWidth variant="outlined">
              <InputLabel>Role</InputLabel>
              <Select
                value={addRole}
                onChange={(e) => setAddRole(e.target.value)}
                label="Role"
                disabled={adding}
              >
                <MenuItem value="viewer">Viewer - Read-only access</MenuItem>
                <MenuItem value="operator">Operator - Can start/stop bot</MenuItem>
                <MenuItem value="manager">Manager - Can edit bot config</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)} disabled={adding}>
              Cancel
            </Button>
            <Button onClick={handleAdd} variant="contained" disabled={adding || !addDiscordId.trim()}>
              {adding ? <CircularProgress size={24} /> : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Role Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => !editing && setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Manager Role</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Change role for {editManager?.username || editManager?.discordId}
            </Typography>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Role</InputLabel>
              <Select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                label="Role"
                disabled={editing}
              >
                <MenuItem value="viewer">Viewer - Read-only access</MenuItem>
                <MenuItem value="operator">Operator - Can start/stop bot</MenuItem>
                <MenuItem value="manager">Manager - Can edit bot config</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={editing}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} variant="contained" disabled={editing}>
              {editing ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => !deleting && setDeleteDialogOpen(false)}
        >
          <DialogTitle>Remove Manager?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove {deleteManager?.username || deleteManager?.discordId} as
              a manager? They will lose access to this bot.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
              {deleting ? <CircularProgress size={24} /> : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default BotManagersPanel;
